import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import AnimatedPulse from "../AnimatedPulse/AnimatedPulse";
import Blush from "@src/stories/client/Blush/Blush";

interface MetaObject {
    percentage: string;
    title: string;
    description: string;
    details: string[];
}

interface ResultsBlockProps {
    data: MetaObject[];
}

export const CaseStudyResults = ({ settings }) => {
    const { caseStudiesData, globals } = settings.settings;

    const results_1 = caseStudiesData.results_1;
    const results_2 = caseStudiesData.results_2;
    const results_3 = caseStudiesData.results_3;

    const {
        title,
        title_font_type,
        tagline,
        tagline_font_type,
        description,
        description_font_type,
        title_font_style = "text-3xl font-semibold",
        subtitle_font_style = "text-gray-600 text-sm",
        list_font_style = "text-[12px]",
        list_alignment = "center", // Default to center
        circle_ring_color = "#899B30" // Default ring color
    } = settings.settings.section || {};

    // Helper function to extract percentage value
    const extractPercentage = (title) => {
        if (!title) return 100;
        const match = title.match(/(\d+(\.\d+)?)%/);
        return match ? parseFloat(match[1]) : 100;
    };

    // Calculate percentages
    const percent1 = extractPercentage(results_1.title);
    const percent2 = extractPercentage(results_2.title);
    const percent3 = extractPercentage(results_3.title);

    return (
        <>
            <div className="flex flex-col mb-9 lg:mb-[88px]">
                {tagline && <h3 className={tagline_font_type}>{tagline}</h3>}
                {title && <h2 className={title_font_type}>{title}</h2>}
                {description && <p className={description_font_type} dangerouslySetInnerHTML={{ __html: description }}></p>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 max-lg:gap-16">
                {/* Result 1 */}
                {results_1.title && <ResultColumn
                    title={results_1.title}
                    subTitle={results_1.sub_title}
                    points={results_1.points}
                    percentage={percent1}
                    titleStyle={title_font_style}
                    subtitleStyle={subtitle_font_style}
                    listStyle={list_font_style}
                    listAlignment={list_alignment}
                    circleRingColor={circle_ring_color}
                globals={globals}
                />}

                {/* Result 2 */}
                {results_2.title && <ResultColumn
                    title={results_2.title}
                    subTitle={results_2.sub_title}
                    points={results_2.points}
                    percentage={percent2}
                    titleStyle={title_font_style}
                    subtitleStyle={subtitle_font_style}
                    listStyle={list_font_style}
                    listAlignment={list_alignment}
                    circleRingColor={circle_ring_color}
                globals={globals}
                />}

                {/* Result 3 */}
                {results_3.title && <ResultColumn
                    title={results_3.title}
                    subTitle={results_3.sub_title}
                    points={results_3.points}
                    percentage={percent3}
                    titleStyle={title_font_style}
                    subtitleStyle={subtitle_font_style}
                    listStyle={list_font_style}
                    listAlignment={list_alignment}
                    circleRingColor={circle_ring_color}
                globals={globals}
                />}
            </div>
        </>
    );
};

// Component for individual result column
const ResultColumn = ({ 
    title, 
    subTitle, 
    points, 
    percentage = 100, 
    titleStyle, 
    subtitleStyle, 
    listStyle,
    listAlignment,
    circleRingColor,
    globals
}) => {
    const [animatedPercentage, setAnimatedPercentage] = useState(0);

    // Animate percentage on load
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimatedPercentage(percentage);
        }, 500);
        return () => clearTimeout(timer);
    }, [percentage]);

    // Calculate circle properties
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const progressOffset = circumference - (animatedPercentage / 100) * circumference;

    // Get alignment classes based on the setting
    const getAlignmentClasses = () => {
        switch(listAlignment) {
            case 'left':
                return 'text-left items-start';
            case 'right':
                return 'text-right items-end';
            case 'center':
            default:
                return 'text-center items-center';
        }
    };

    return (
        <div className={`flex flex-col ${getAlignmentClasses()}`}>
            {/* Circular Percentage Area */}
            <div className="w-[250px] h-[250px] flex items-center justify-center rounded-full  relative">
                <span className={`${titleStyle} relative`}>{title}</span>

                <AnimatedPulse 
                    settings={{
                        ...globals.pulse_animation,
                        className: "absolute top-0 w-full h-full"
                    }}
                />
                <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 120 120">
                    
                    <circle
                        className="text-gray-300"
                        strokeWidth="4"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="60"
                        cy="60"
                    />
                    <circle
                        className="text-green-600"
                        strokeWidth="4"
                        strokeDasharray={circumference}
                        strokeDashoffset={progressOffset}
                        strokeLinecap="round"
                   
                        stroke={circleRingColor}
                        fill="transparent"
                        r={radius}
                        cx="60"
                        cy="60"
                        style={{
                            transition: 'stroke-dashoffset 1s ease-in-out'
                        }}
                    />
                </svg>
                {/* Animated Rings */}
                {/* Percentage Text */}
            </div>

            {/* Description */}
            <Paragraph 
                text={subTitle} 
                maxChars={500}
                classes={`mb-[40px] mt-[40px] w-[200px] min-h-[80px] ${subtitleStyle}`} 
            />

            {/* Details List */}
            <div className={`flex flex-col gap-[35px] w-full lg:w-[240px] ${listStyle}`}>
                {points && points.map((detail, detailIndex) => (
                    <div key={detailIndex} className="relative flex items-center group">
                        {/* <ListDot /> */}
                        <div className="relative flex items-center">
                            <Blush size={300} color="#D6CFF8"/>
                            <span className="mx-2 group-hover:text-black">{detail.point_value}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Dot element for lists
const ListDot = () => (
    <div className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0 group-hover:bg-black  transition-colors duration-300"></div>
);


export default CaseStudyResults;